import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAppDispatch } from 'redux/store';
import { Alert, Button, ButtonGroup, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectHour12Option } from 'redux/selectors/userSettings';
import { SanitizedHtml } from 'components/SanitizedHtml/SanitizedHtml';

import { WorkflowDetailsModel } from './WorkflowDetailsModel';

/**
 * A workflow action's message is job-produced HTML, rendered by `SanitizedHtml`. These rewrites are the older cosmetic
 * fix that turns line breaks into `<br>` and makes a wide table scroll inside the panel instead of overflowing it. They
 * change the input, so they run here, before the component sanitises: the sanitiser keeps the `position: relative` they
 * add, whereas its normalised `style` attributes would no longer match these patterns.
 */
const messageMarkup = (message: string): string =>
  message
    .replace(/\n/g, '<br>')
    .replace(
      /<div style="overflow-x: auto; max-width: 100%;">/g,
      '<div style="overflow-x: auto; max-width: 100%; width: 100%; display: block; box-sizing: border-box; position: relative;">'
    )
    .replace(
      /<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; min-width: 100%;">/g,
      '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: max-content; min-width: 100%; table-layout: fixed; white-space: nowrap;">'
    );

/**
 * The panel clips a message at its column, as it always has: `overflow: hidden` overrides `SanitizedHtml`'s scrolling
 * default, and a wide table scrolls inside the `overflow-x: auto` wrapper the rewrites above give it.
 */
const messageStyle: React.CSSProperties = { maxWidth: '100%', overflow: 'hidden', display: 'block', width: '100%', boxSizing: 'border-box' };

type Props = {
  workflow_id: number;
};

/**
 * A pure view over {@link WorkflowDetailsModel}, which holds the workflow, the operator's unsaved input and the writes.
 *
 * Keyed by id, because a model reads one workflow for its lifetime: each workflow gets its own model and its own drafts.
 */
const WorkflowDetailsPanel: React.FC<Props> = (props) => <WorkflowDetails key={props.workflow_id} {...props} />;

const WorkflowDetails = observer(({ workflow_id }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const hour12 = useSelector(selectHour12Option);

  const [model] = useState(() => new WorkflowDetailsModel(workflow_id, dispatch));
  const busy = model.busy;

  useEffect(() => model.markAsRead(), [model]);

  const body = (): React.ReactNode => {
    const workflow = model.workflow;
    if (workflow === 'loading') return <Spinner animation="border" />;
    if (workflow === 'not-found') return <p className="m-0">{t('workflows.not_found', { id: workflow_id })}</p>;
    if (workflow instanceof Error) return <p className="m-0 text-danger">{t('workflows.load_failed', { id: workflow_id, error: workflow.message })}</p>;
    const writeError = model.writeError;
    return (
      <>
        {writeError && (
          <Alert variant="danger" dismissible onClose={() => model.dismissWriteError()}>
            {t('workflows.write_failed', { error: writeError.message })}
          </Alert>
        )}
        <div className="mb-4">
          <h3>
            WORKFLOW {workflow.workflow_id}
            {busy && <Spinner animation="border" size="sm" className="ms-2" role="status" aria-label={t('workflows.refreshing')} />}
          </h3>

          <p>
            <strong>{t('subject')}: </strong>
            {workflow.workflow_subject}
          </p>

          <p>
            <strong>{t('category')}: </strong>
            {workflow.workflow_category}
          </p>

          <p>
            <strong>{t('created_at')}: </strong>
            {new Date(workflow.created_at).toLocaleString('nl-NL', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12,
            })}
          </p>

          <p>
            <strong>{t('workflows.last_updated')}: </strong>
            {new Date(workflow.updated_at).toLocaleString('nl-NL', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12,
            })}
          </p>
        </div>

        <Table hover size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '80px' }}>ID</th>
              <th style={{ width: '150px' }}>{t('date')}</th>
              <th style={{ width: 'auto' }}>Details</th>
            </tr>
          </thead>

          <tbody>
            {workflow.workflows_actions.map((action) => (
              <tr key={action.workflow_action_id}>
                <td style={{ width: '80px' }}>{action.workflow_action_id}</td>

                <td style={{ width: '150px' }}>
                  {new Date(action.created_at).toLocaleString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12,
                  })}
                </td>

                <td
                  style={{
                    width: 'auto',
                    maxWidth: '0',
                    wordWrap: 'break-word',
                  }}
                >
                  {action.response ? (
                    <>
                      <p>
                        <strong>{t('workflows.chosen_option')}: </strong>
                        {action.response.label} ({action.response.user ? `${action.response.user} ` : ''}
                        {new Date(action.response.timestamp).toLocaleString('nl-NL', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12,
                        })}
                        )
                      </p>

                      {Array.isArray(action.response_inputs) && action.response_inputs.length > 0 && (
                        <div>
                          {action.response_inputs.map((input) => (
                            <Form.Group key={input.label} className="mb-2">
                              <Form.Label>{input.label}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                disabled
                                value={input.value || t('empty')}
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  color: '#495057',
                                }}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}

                      {action.dropdown_parameters && action.dropdown_parameters.length > 0 && (
                        <div className="mt-3">
                          {action.dropdown_parameters.map((param) => {
                            const originalVal = param.value ?? param.default_expression ?? '';
                            const selectedVal = param.out_value ?? param.value ?? param.default_expression ?? '';
                            const hasChanged = param.out_value && param.out_value !== param.value;

                            return (
                              <p key={param.parameter_id}>
                                <strong>{param.name}: </strong>
                                {hasChanged ? (
                                  <span>
                                    <span
                                      style={{
                                        textDecoration: 'line-through',
                                        color: '#666',
                                      }}
                                    >
                                      {originalVal || t('empty')}
                                    </span>
                                    {' → '}
                                    <span
                                      style={{
                                        color: '#28a745',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {selectedVal || t('empty')}
                                    </span>
                                  </span>
                                ) : (
                                  selectedVal || t('empty')
                                )}
                              </p>
                            );
                          })}
                        </div>
                      )}

                      <p>
                        <strong>{t('workflows.original_message')}: </strong>
                        <br />
                      </p>

                      <p>
                        <strong>{t('subject')}: </strong>
                        {action.subject || t('empty')}
                      </p>

                      <p>
                        <strong>{t('message')}: </strong>
                        <br />
                        <SanitizedHtml html={messageMarkup(action.message || t('empty'))} style={messageStyle} />
                      </p>
                    </>
                  ) : (
                    <div>
                      <div className="d-flex justify-content-between">
                        <strong>{t('workflows.chosen_option')}: </strong>

                        <div className="ms-auto">
                          <ButtonGroup>
                            {action.response_options?.map((option) => (
                              <Button
                                key={option.id}
                                variant="outline-primary"
                                disabled={busy}
                                onClick={() => model.respond(action.workflow_action_id, option.id)}
                              >
                                {option.label}
                              </Button>
                            ))}
                          </ButtonGroup>
                        </div>
                      </div>

                      <p>
                        <strong>{t('subject')}: </strong>
                        {action.subject || t('empty')}
                      </p>

                      <p>
                        <strong>{t('message')}: </strong>
                        <br />
                        <SanitizedHtml html={messageMarkup(action.message || t('empty'))} style={messageStyle} />
                      </p>

                      {action.dropdown_parameters && action.dropdown_parameters.length > 0 && (
                        <div className="mt-3">
                          {action.dropdown_parameters.map((param) => {
                            const options = String(param.options || '')
                              .split(',')
                              .map((opt) => opt.trim())
                              .filter(Boolean);

                            return (
                              <Form.Group key={param.parameter_id} className="mb-2">
                                <Form.Label>{param.name}</Form.Label>
                                <Form.Select
                                  id={`${action.workflow_action_id}-dropdown-${param.name}`}
                                  value={model.inputValue(action.workflow_action_id, 'dropdown', param.name)}
                                  onChange={(e) => model.setInput(action.workflow_action_id, 'dropdown', param.name, e.target.value)}
                                >
                                  {options.map((opt, idx) => (
                                    <option key={idx} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            );
                          })}
                        </div>
                      )}

                      {action.response_input_options && action.response_input_options.length > 0 && (
                        <div>
                          {action.response_input_options.map((opt) => (
                            <Form.Group key={opt.label} className="mb-2">
                              <Form.Label>{opt.label}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                id={`${action.workflow_action_id}-input-${opt.label}`}
                                value={model.inputValue(action.workflow_action_id, 'input', opt.label)}
                                onChange={(e) => model.setInput(action.workflow_action_id, 'input', opt.label, e.target.value)}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}

                      {model.canSave(action.workflow_action_id) && (
                        <Button variant="primary" disabled={busy} onClick={() => model.save(action.workflow_action_id)}>
                          {t('save')}
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  return (
    <div
      style={{
        padding: '0.5rem',
        overflow: 'auto',
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
      }}
    >
      {body()}
    </div>
  );
});

export default WorkflowDetailsPanel;
